/**
 * `rel` values this repo emits -- on `<link>` and on the `<a target="_blank">` anchors that leave the app.
 *
 * `modulepreload` rather than `preload as="script"` is a DECISION, not a spelling: the MFE bundle is an ES module, and
 * the plain preload form fetches it a second time. Two surfaces emit it -- the shared `MfeModulePreload` component's
 * props and the admin shell's imperative loader, which builds the `<link>` element by hand -- so the token is homed
 * here rather than spelled once per surface.
 */
export const LINK_REL_MODULEPRELOAD = 'modulepreload';

/**
 * The `rel` every `target="_blank"` anchor carries. BOTH tokens are the decision, not one: `noopener` severs
 * `window.opener` so the opened tab cannot navigate this one, and `noreferrer` withholds the `Referer` header (which
 * on a property/report link would leak the internal path the operator was on).
 *
 * EXPORTED, AND EITHER SPELLING IS SAFE -- FINAL ADJUDICATION 2026-08-23, superseding the module-private reversal
 * taken earlier the same day. The history, because the reasoning is still what makes the outcome correct: the const
 * was exported and read at ~19 anchors on the reasoning that an SSOT beats a repeated literal, and that turned every
 * one of those anchors into a standing `react/jsx-no-target-blank` ERROR -- upstream's hasSecureRel accepts only a
 * string literal and resolves neither an imported binding nor a member load. Worse than the noise: with the literal
 * gone that matcher could no longer catch the case it exists for, an anchor carrying NO `rel` at all. So the const
 * was made module-private and the anchors put back on the literal.
 *
 * That argument held against `react/jsx-no-target-blank` ONLY, and the repo no longer runs it. `eslint.config.ts`
 * replaces it with the local `custom/require-secure-target-blank-rel`
 * (`tools/linting/eslint-rules/require-secure-target-blank-rel.ts`), which RESOLVES the SSOT identifier
 * `rel={REL_NOOPENER_NOREFERRER}` and also accepts the literal carrying both tokens -- while still convicting a
 * missing `rel`, a half-token literal such as `rel="noreferrer"` alone (the exact drift the hoist was minted to
 * stop, and a case the old rule let pass), and any other unresolvable expression. With that matcher live, both
 * spellings are enforced and the export blinds nothing, so the export is restored.
 *
 * WHAT THE TREE SPELLS TODAY, 2026-09-06: every `target="_blank"` anchor imports this const. The counter-adjudication
 * this paragraph used to carry -- "a migration back to the identifier would buy nothing", on the ground that the local
 * matcher sanctions the literal -- answered the wrong question, and it is recorded here rather than deleted because
 * the confusion is easy to re-derive. That the literal is SAFE is not that the literal is not WASTE: the two tokens
 * are ONE decision, 23 anchors spelling it inline made that decision spelled 24 times, and adding a third token would
 * have meant 24 edits with the rule able only to redden the 23 it had already made wrong. The migration landed with
 * the `rel` un-foreclosure below; the anchors now READ the decision, and the local rule still derives its required
 * token set from this same export, so both sides move together from one edit.
 *
 * THE FORECLOSURE THAT DEMANDED THE FIRST HOIST IS NOW REOPENED FOR `rel`, AND FOR `rel` ONLY.
 * `QUAL-JSX-ATTR-CONST-ADOPTION-001` skips every lint-anchored attribute through `const/jsx-lint-anchored-attrs.ts`,
 * the same set the dedup engine holds as a hard site foreclosure. `rel` LEFT that roster on 2026-09-06 because its
 * only ground was the retired upstream rule, while `target` stays because the local rule reads the target literal to
 * decide whether to run at all. The foreclosure's job is unchanged -- stop a scanner DEMANDING const adoption on an
 * attribute a linter reads -- and it is now narrower by exactly one name that no linter reads any more.
 */
export const REL_NOOPENER_NOREFERRER = 'noopener noreferrer';

/**
 * The same decision in `window.open`'s windowFeatures grammar, which is COMMA-separated rather than space-separated --
 * a different spelling of one value, so it derives from it rather than restating it.
 */
export const WINDOW_OPEN_FEATURES_EXTERNAL = REL_NOOPENER_NOREFERRER.replace(' ', ',');
